import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { blockerService, businessEmployeeService, businessHolidayService, businessService, feedback } from '../../../shared/services';
import { Api } from '../../../shared/services/api/axios-config';
import { setCsrfRequestToken } from '../../../shared/services/api/axios-config/interceptors';
import { DatePicker, DayScheduleEditor, useScheduleState, scheduleToApiDto, validateScheduleDays } from '../../../shared/components';
import { IPropsBusinessPage } from '../Business';
import { dateFormat } from '../../../shared/helpers/dateFormat';

interface IProps extends IPropsBusinessPage {
  mode?: 'full' | 'schedule' | 'holidays';
}

export const BusinessHoursConfig: React.FC<IProps> = ({ business, refetch, mode = 'full' }) => {
  const queryClient = useQueryClient();
  const [days, setDays] = useScheduleState(business.weeklySchedules);
  const [holidayDate, setHolidayDate] = useState<Dayjs | null>(null);
  const [holidayDesc, setHolidayDesc] = useState('');
  const holidayBlockerMap = useRef<Map<number, number>>(new Map());

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [affectedEmployees, setAffectedEmployees] = useState<string[]>([]);

  const { data: holidays, isFetching: isFetchingHolidays } = useQuery({
    queryKey: ['businessHolidays', business.id],
    queryFn: () => businessHolidayService.getHolidays(business.id),
  });

  const doSave = useCallback(async () => {
    const err = validateScheduleDays(days);
    if (err) { feedback(err, 'error'); return; }

    const weeklySchedules = scheduleToApiDto(days);

    await businessService.update({
      id: business.id,
      name: business.name,
      address: business.address,
      cnpj: business.cnpj,
      profilePicture: business.profilePicture,
      slug: business.slug,
      hideFromSearch: business.hideFromSearch,
      cardFeeDebit: business.cardFeeDebit,
      cardFeeCredit: business.cardFeeCredit,
      appointmentLeadTimeMinutes: business.appointmentLeadTimeMinutes,
      weeklySchedules,
    });
  }, [business, days]);

  const { mutate: saveSchedule, isLoading: isSaving } = useMutation(doSave, {
    onSuccess: () => {
      feedback('Horários salvos com sucesso', 'success');
      refetch();
    },
    onError: (e: Error) => feedback(e.message, 'error'),
  });

  // Check for employees with hours outside the new business hours
  const handleSaveClick = useCallback(async () => {
    try {
      const employees = await businessEmployeeService.get(business.id);

      const affected: string[] = [];

      for (const emp of employees) {
        if (!emp.weeklySchedules) continue;
        for (const ws of emp.weeklySchedules) {
          if (ws.isClosed) continue;
          const newDay = days.find(d => d.dayOfWeek === ws.dayOfWeek);
          if (!newDay || newDay.isClosed) continue;
          if (
            (ws.startTime && newDay.startTime && ws.startTime < newDay.startTime) ||
            (ws.endTime && newDay.endTime && ws.endTime > newDay.endTime)
          ) {
            affected.push(emp.name || emp.employee?.user?.name || `#${emp.id}`);
            break;
          }
        }
      }

      if (affected.length > 0) {
        setAffectedEmployees(affected);
        setConfirmModalOpen(true);
      } else {
        saveSchedule();
      }
    } catch (e: unknown) {
      // If fetching employees fails, just try to save anyway
      saveSchedule();
    }
  }, [business.id, days, saveSchedule]);

  const { mutate: addHoliday, isLoading: isAddingHoliday } = useMutation(
    async () => {
      try {
        const tokenRes = await Api.get('/auth/antiforgery-token');
        if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      } catch {
        // best-effort
      }
      const holiday = await businessHolidayService.addHoliday(business.id, holidayDate!.toDate(), holidayDesc || 'Feriado');
      const dateStr = holidayDate!.format('YYYY-MM-DD');
      const holidayDow = holidayDate!.day(); // dayjs .day() = 0 (domingo) a 6 (sábado)
      const holidaySched = days.find(d => d.dayOfWeek === holidayDow);
      const startH = holidaySched?.startTime ?? '08:00';
      const endH = holidaySched?.endTime ?? '18:00';
      const blockerId = await blockerService.create({
        startDate: dateStr,
        endDate: dateStr,
        startTime: startH.substring(0, 5),
        endTime: endH.substring(0, 5),
        businessId: business.id,
        businessEmployeeId: undefined,
        daysOfWeek: [],
        reason: 'Feriado: ' + holiday.description,
      });
      holidayBlockerMap.current.set(holiday.id, blockerId);
      return holiday;
    },
    {
      onSuccess: () => {
        feedback('Feriado adicionado', 'success');
        setHolidayDate(null);
        setHolidayDesc('');
        queryClient.invalidateQueries({ queryKey: ['businessHolidays', business.id] });
        queryClient.invalidateQueries({ queryKey: ['blockers', business.id] });
      },
      onError: (e: Error) => feedback(e.message, 'error'),
    }
  );

  const { mutate: removeHoliday } = useMutation(
    async (holidayId: number) => {
      try {
        const tokenRes = await Api.get('/auth/antiforgery-token');
        if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      } catch {
        // best-effort
      }
      const blockerId = holidayBlockerMap.current.get(holidayId);
      if (blockerId) {
        await blockerService.remove(blockerId);
        holidayBlockerMap.current.delete(holidayId);
      }
      await businessHolidayService.removeHoliday(business.id, holidayId);
    },
    {
      onSuccess: () => {
        feedback('Feriado removido', 'success');
        queryClient.invalidateQueries({ queryKey: ['businessHolidays', business.id] });
        queryClient.invalidateQueries({ queryKey: ['blockers', business.id] });
      },
      onError: (e: Error) => feedback(e.message, 'error'),
    }
  );

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {(mode === 'full' || mode === 'schedule') && (
        <Box display="flex" flexDirection="column" gap={2}>
          <DayScheduleEditor
            schedule={days}
            onChange={setDays}
            defaultStartTime={dayjs('08:00', 'HH:mm')}
            defaultEndTime={dayjs('18:00', 'HH:mm')}
          />
          <Box display="flex" justifyContent="end">
            <Button variant="contained" onClick={handleSaveClick} disabled={isSaving}>
              {isSaving ? <CircularProgress size={20} /> : 'Salvar Horários'}
            </Button>
          </Box>

          {/* Confirmation modal when employees are affected */}
          <Dialog open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Horários de profissionais serão ajustados</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Há {affectedEmployees.length} profissional(is) que trabalha(m) em horários fora do novo horário do estabelecimento:
              </DialogContentText>
              <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
                {affectedEmployees.map((name, i) => (
                  <Typography key={i} component="li" variant="body2">{name}</Typography>
                ))}
              </Box>
              <DialogContentText>
                Ao salvar, os horários desses profissionais serão ajustados automaticamente para dentro do novo horário. Deseja continuar?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmModalOpen(false)}>Cancelar</Button>
              <Button variant="contained" onClick={() => { setConfirmModalOpen(false); saveSchedule(); }}>
                Continuar
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      {(mode === 'full' || mode === 'holidays') && (
        <Box>
          <Typography variant="h6" gutterBottom>Adicionar feriado</Typography>
          <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ width: 220 }}>
              <DatePicker label="Data do feriado" value={holidayDate}
                onChange={(value: Dayjs | null) => setHolidayDate(value)} fullWidth size="small" />
            </Box>
            <TextField label="Descrição (opcional)" value={holidayDesc}
              onChange={e => setHolidayDesc(e.target.value)} sx={{ width: 220 }} />
            <Button variant="outlined" size="small" onClick={() => addHoliday()}
              disabled={!holidayDate || isAddingHoliday} sx={{ height: 40 }}>Adicionar</Button>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            {isFetchingHolidays
              ? <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              : holidays && holidays.length > 0
                ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell>Descrição</TableCell>
                        <TableCell width={40} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {holidays.map(h => (
                        <TableRow key={h.id}>
                          <TableCell>{dateFormat(h.date, 'DD/MM/YYYY')}</TableCell>
                          <TableCell>{h.description}</TableCell>
                          <TableCell>
                            <Button size="small" color="error" onClick={() => removeHoliday(h.id)}>Remover</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
                : <Typography color="text.secondary">Nenhum feriado cadastrado.</Typography>}
          </Paper>
        </Box>
      )}
    </Box>
  );
};
